import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { captureException } from '@sentry/nextjs'

import appConfig from '~/config/app'
import ROUTES from '~/shared/constants/routes.const'
import { RegisterFields, SignUpScenario } from '~/types/sign-up'
import { cleanNullAndUndefined2 } from '~/utils'
import { updateToken, isAuthenticated } from '~/auth/slices/auth.slice'
import useCurrentUser from '~/hooks/useCurrentUser'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { useRegisterUserMutation } from '~/auth/apis/auth.api'
import { useUpdateUserMutation } from '~/redux/apis/user-api'
import { selectCaptcha, setCaptcha, setAuthType, resetCaptcha } from '~/auth/slices/recaptcha.slice'
import { AuthTypes } from '~/auth/types/auth.types'
import accountAnalytics from '~/analytics/account/accountAnalytics'
import useGetFirstOnboardingPage from '~/onboarding/utils/useGetFirstOnboardingPage'
import { AuthenticationResponse, Result } from '~/redux/apis/types'
import { getCookie } from '~/utils/cookie.utils'
import { getPreSignupUsState } from '~/curriculum/constants/usCurriculumStates.const'
import { selectCurriculumName } from '~/curriculum/slice/persistCurriculum.slice'

import { trackingEventStore } from '~/behavior-tracking/utility/tracking-events-store'

import { setIsPreSignupActionCreator } from '~/onboarding/redux/onboarding.slice'

import { isBookingCheckoutScenario, isCheckoutScenario, isValidRegisterRequest } from '../utils/register.utils'

import useReCaptchaV3 from './useReCaptchaV3'
import useRegisterContext from './useRegisterContext'
import useValidateRegisterFields from './useValidateRegisterFields'
import useCheckSubscriptionAndBookingStatus from './useSubscriptionAndBookingStatus'

interface UseRegister {
  register: () => void
  fields: RegisterFields
  loading: boolean
  registeredUser: Result<AuthenticationResponse> | undefined
  isSuccess: boolean
}

export function useRegister(): UseRegister {
  const router = useRouter()
  const { getReCaptchaToken } = useReCaptchaV3()

  const { fields, setErrors, signUpScenario, setBackendError } = useRegisterContext()

  const [loading, setLoading] = useState(false)
  const user = useCurrentUser()
  const authenticated = useAppSelector(isAuthenticated)

  const pathToFirstOnboardingPage = useGetFirstOnboardingPage()
  const { runCallbackWithSubscriptionAndBookingStatus } = useCheckSubscriptionAndBookingStatus()

  const isBookingCheckout = isBookingCheckoutScenario(signUpScenario)
  const isCheckout = isCheckoutScenario(signUpScenario)
  const dispatch = useAppDispatch()

  const validateRegisterFields = useValidateRegisterFields()
  const [registerUser, { data: registeredUser, isLoading, isSuccess, error }] = useRegisterUserMutation()
  const [updateUserData, { data: userUpdate, error: userUpdateError }] = useUpdateUserMutation()
  // Its own instance: a failed state save must not raise the registration error banner.
  const [saveCurriculumState] = useUpdateUserMutation()
  const persistentCurriculumName = useAppSelector(selectCurriculumName)
  const captcha = useAppSelector(selectCaptcha)

  const redirectAfterRegistration = useCallback(
    (redirectFrom: string, isSubscriptionRequired: boolean, hasBookings: boolean) => {
      if (redirectFrom && (redirectFrom.startsWith('booking') || redirectFrom.startsWith('/no/campaigns/dnb'))) {
        const pathnames: Record<string, string> = {
          'booking-for-self': ROUTES.BOOKING_FOR_SELF,
          'booking-for-other': ROUTES.BOOKING_FOR_OTHER,
          '/no/campaigns/dnb/saga-private-banking': ROUTES.DNB_CAMPAIGN_TOD,
        }

        router.push({
          // Booking and the DNB tutoring campaign 404 in the school platform. Consumed here rather than
          // skipped, or the `if (redirectFrom)` arm below would push `booking-for-self` as a path.
          pathname: appConfig.isSchoolPlatform ? ROUTES.DASHBOARD : pathnames[redirectFrom],
          query: {
            didAuthenticate: true,
          },
        })

        return
      }

      if (redirectFrom) {
        router.push(redirectFrom)
      } else if (isSubscriptionRequired && hasBookings && !appConfig.isSchoolPlatform) {
        // The Student Panel is a Tutor-on-Demand surface and 404s in the school platform.
        router.push(ROUTES.STUDENT_PANEL)
      } else {
        // go to birth year page or to get started page
        router.push(pathToFirstOnboardingPage)
      }
    },
    [pathToFirstOnboardingPage, router],
  )

  useEffect(() => {
    if (userUpdateError) {
      setBackendError(userUpdateError.data?.errorCode || 'unknown')
      setLoading(false)
    } else if (isCheckout) {
      dispatch(resetCaptcha())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, dispatch, isCheckout, userUpdate, userUpdateError])

  useEffect(() => {
    const redirectFrom = router.query.from as string

    if (isSuccess && !isLoading && registeredUser?.type === 'success') {
      const userInfo = registeredUser.data.user
      const userId = userInfo.id
      accountAnalytics.logSignup(userId, registeredUser.data.log)
      dispatch(updateToken(registeredUser.data))
      dispatch(setIsPreSignupActionCreator(false))

      // Register derives both columns from the IP, so a pre-signup pick must overwrite both.
      const preSignupState = getPreSignupUsState(persistentCurriculumName)
      if (preSignupState?.curriculumCountryCode) {
        saveCurriculumState({
          data: {
            curriculumCountryCode: preSignupState.curriculumCountryCode,
            curriculumStateCode: preSignupState.stateCode,
          },
        })
          .unwrap()
          .catch((err) => captureException(err, { tags: { feature: 'curriculum', action: 'save-state-on-signup' } }))
      }

      if (!isCheckout || isBookingCheckout) {
        runCallbackWithSubscriptionAndBookingStatus(userInfo, (isSubscriptionRequired, hasBookings) =>
          redirectAfterRegistration(redirectFrom, isSubscriptionRequired, hasBookings),
        )
      }
    }

    if (error) {
      setLoading(false)
      if ('data' in error) {
        const { data } = error

        if (data?.details && data.details.captchaChallenge) {
          setBackendError('')
          // if (captcha.answer) {
          //   setErrors({ captchaAnswer: t('captchaMismatch') })
          // }
          dispatch(setCaptcha({ answer: '', challenge: data.details.captchaChallenge }))
        } else if (data?.details && data.details.email) {
          setBackendError('email_invalid')
        } else if (data.message) {
          setBackendError(data.message)
        } else if (typeof data === 'string') {
          setBackendError(data)
        } else {
          setBackendError('unknown')
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    error,
    isBookingCheckout,
    isCheckout,
    isLoading,
    isSuccess,
    persistentCurriculumName,
    registeredUser,
    router,
    saveCurriculumState,
    setBackendError,
    setErrors,
  ])

  const register = useCallback(async () => {
    if (loading) return
    setLoading(true)
    let recaptchaToken = ''

    if (!authenticated) {
      recaptchaToken = (await getReCaptchaToken(`signup/${AuthTypes.EMAIL}`)) as string
      if (!recaptchaToken) {
        setLoading(false)
        setBackendError('ReCaptcha blocked')
        return
      }
    }

    const curriculumCountryCode = getCookie('curriculumCountry')

    setErrors({})
    const registerErrors = validateRegisterFields()
    setErrors(registerErrors)

    if (Object.keys(registerErrors).length) {
      setLoading(false)
      return
    }
    const submitFields = cleanNullAndUndefined2({
      ...fields,
      scenario: isBookingCheckout
        ? SignUpScenario.BOOKING
        : isCheckout
        ? SignUpScenario.SUBSCRIPTION
        : SignUpScenario.DEFAULT,
      birthYear: fields.birthYear ? Number(fields.birthYear) : undefined,
      countryCallingCode: undefined,
      countryCode: undefined,
      authEmail: undefined,
      email: fields.email,
      gradeLevelId: fields.gradeLevelId ? Number(fields.gradeLevelId) : undefined,
      phone: fields.phone ? `${fields.countryCallingCode}:${fields.phone}` : undefined,
      skill: typeof fields.skill === 'number' ? fields.skill : undefined,
      yearsInSchool: typeof fields.yearsInSchool === 'number' ? fields.yearsInSchool : undefined,
      wantsNewsletter: fields.wantsNewsletter || false,
      curriculumCountryCode,
    })

    if (submitFields.passwordConfirm) {
      delete submitFields.passwordConfirm
    }

    if (!authenticated) {
      if (isValidRegisterRequest(submitFields, submitFields.scenario)) {
        dispatch(setAuthType(AuthTypes.EMAIL))
        const pseudoId = await trackingEventStore.getPseudoId()
        await registerUser({
          recaptchaToken,
          pseudoId,
          captchaAnswer: captcha.answer,
          ...(submitFields as RegisterFields),
        })
      }
    } else if (user) {
      delete submitFields.scenario

      updateUserData({ data: submitFields })
    }
  }, [
    authenticated,
    captcha.answer,
    dispatch,
    fields,
    getReCaptchaToken,
    isBookingCheckout,
    isCheckout,
    loading,
    registerUser,
    setBackendError,
    setErrors,
    updateUserData,
    user,
    validateRegisterFields,
  ])

  return { register, fields, loading, isSuccess, registeredUser }
}
